import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Modal, Alert, Row, Col, Spinner } from 'react-bootstrap';
import {
  FaPlus, FaSyncAlt, FaFilter, FaSearch, FaEdit, FaTrash, FaPhoneAlt, FaWhatsapp
} from 'react-icons/fa';
import * as XLSX from 'xlsx';
import KpiCard from '../components/KpiCard';
import api from '../services/api';
import TelephoneImportModal from './TelephoneImportModal';
import TableSkeleton from '../components/TableSkeleton';
import { DemandeNumeroModal, FicheRemiseModal } from './TelephonePdfDocs';

const MAIN      = '#6d4e36';
const OPERATEURS = ['IAM', 'INWI', 'ORANGE'];
const STATUTS    = ['ok', 'suspendu', 'résilié', 'en attente'];

/* couleurs connues par société */
const SOC_COLORS = [
  { name: 'pistas',      color: '#1565c0' },
  { name: 'rutas',       color: '#2e7d32' },
  { name: 'sepgb',       color: '#6f42c1' },
  { name: 'elite parcs', color: '#0d9488' },
  { name: 'cadex',       color: '#e67e22' },
  { name: 'brostol',     color: '#c0392b' },
  { name: 'siege',       color: '#6d4e36' },
];
const EXTRA_COLORS = ['#546e7a', '#00796b', '#ad1457', '#283593', '#558b2f'];
const OPER_COLORS  = { INWI: '#8e44ad', MEDITEL: '#2980b9' };

/* Formate un numéro marocain pour wa.me (212XXXXXXXXX) */
const toWaNumber = (tel) => {
  const d = String(tel).replace(/\D/g, '');
  if (d.startsWith('212')) return d;
  if (d.startsWith('0'))   return '212' + d.slice(1);
  return '212' + d;
};

/* ── MiniChip : petite card filtre ── */
const MiniChip = ({ label, value, color, active, onClick }) => (
  <div onClick={onClick} style={{
    display: 'inline-flex', alignItems: 'center', gap: 6,
    border: `1.5px solid ${active ? color : color + '66'}`,
    borderRadius: 20, padding: '3px 11px',
    cursor: 'pointer',
    background: active ? color + '18' : '#fff',
    transition: 'all 0.15s',
    userSelect: 'none',
  }}>
    <span style={{ fontWeight: 800, fontSize: '0.82rem', color }}>{value}</span>
    <span style={{ fontSize: '0.78rem', fontWeight: active ? 700 : 500, color: active ? color : '#555' }}>{label}</span>
  </div>
);

/* ── MiniGroup : ligne étiquette + chips ── */
const MiniGroup = ({ label, children }) => (
  <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
    <span style={{
      fontSize: '0.68rem', fontWeight: 700, color: '#bbb',
      textTransform: 'uppercase', letterSpacing: '0.06em',
      minWidth: 62, flexShrink: 0
    }}>{label} :</span>
    {children}
  </div>
);

/* ── Logos opérateurs ── */
const OPER_LOGOS = {
  ORANGE: '/logos/orange.png',
  IAM:    '/logos/iam.jpg',
};

/* ── Card opérateur avec logo image ── */
const OperatorKpiCard = ({ operateur, value, color, active, onClick }) => {
  const [hovered, setHovered] = React.useState(false);
  const [imgError, setImgError] = React.useState(false);
  const logo = OPER_LOGOS[operateur];

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        borderRadius: 14,
        border: active ? `2px solid ${color}` : `1.5px solid ${color}28`,
        borderTop: `4px solid ${color}`,
        background: hovered ? `${color}12` : '#fff',
        padding: '1rem 1.1rem 0.9rem',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        transition: 'transform 0.17s ease, box-shadow 0.17s ease, background 0.17s ease',
        transform: hovered ? 'translateY(-4px)' : 'none',
        boxShadow: hovered
          ? `0 10px 28px ${color}35`
          : active ? `0 4px 14px ${color}25` : '0 2px 8px rgba(0,0,0,0.06)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Logo en filigrane */}
      {logo && !imgError && (
        <img
          src={logo}
          alt={operateur}
          onError={() => setImgError(true)}
          style={{
            position: 'absolute', bottom: 6, right: 8,
            height: 36, width: 'auto', maxWidth: 80,
            objectFit: 'contain', opacity: 0.12,
            pointerEvents: 'none',
          }}
        />
      )}
      {/* Titre */}
      <div style={{
        fontSize: '0.7rem', color: '#999', textTransform: 'uppercase',
        letterSpacing: '0.06em', fontWeight: 700, marginBottom: 6,
        display: 'flex', alignItems: 'center', gap: 6,
      }}>
        {logo && !imgError && (
          <img src={logo} alt={operateur} onError={() => setImgError(true)}
            style={{ height: 14, width: 'auto', maxWidth: 48, objectFit: 'contain', opacity: 0.7 }} />
        )}
        {imgError && operateur}
      </div>
      {/* Valeur */}
      <div style={{ fontSize: '1.55rem', fontWeight: 800, color, lineHeight: 1.1 }}>
        {value}
      </div>
    </div>
  );
};

const EMPTY_FORM = {
  societe: '', operateur: '', tel: '', colonne1: '',
  nom_prenom: '', qualification: '', statut: 'ok',
  bureau: '', forfait: '', prix: '', observation: '', ancien_utilisateur: ''
};

/* ─── styles ──────────────────────────────────────────────────────────────── */
const CSS = `
  .tel-table { width:100%; border-collapse:separate; border-spacing:0; }
  .tel-th {
    background: linear-gradient(135deg,#6d4e36 0%,#8b5e3c 100%);
    color:#fff; font-weight:600; font-size:0.75rem;
    text-transform:uppercase; letter-spacing:0.05em;
    padding:11px 13px; white-space:nowrap;
    user-select:none; cursor:pointer; position:sticky; top:0; z-index:2;
    transition: background 0.15s;
  }
  .tel-th:hover { background: linear-gradient(135deg,#5a3e2a 0%,#7a4e30 100%); }
  .tel-th:first-child { border-radius:0; }
  .tel-tr { transition: background 0.12s, box-shadow 0.12s; }
  .tel-tr:hover { background:#f5ede8 !important; box-shadow: inset 3px 0 0 #6d4e36; }
  .tel-tr:hover .tel-td { border-bottom-color:#eeddd0; }
  .tel-td { padding:10px 13px; font-size:0.84rem; vertical-align:middle; border-bottom:1px solid #f5ede8; transition:border-color 0.12s; }
  .tel-action-btn {
    border:none; background:none; border-radius:8px;
    padding:5px 7px; cursor:pointer; font-size:0.82rem;
    transition: background 0.13s, transform 0.1s; line-height:1;
    display:inline-flex; align-items:center; justify-content:center;
  }
  .tel-action-btn:hover { transform: scale(1.15); }
  .tel-action-btn.edit   { color:#6d4e36; }
  .tel-action-btn.edit:hover   { background:rgba(109,78,54,0.12); }
  .tel-action-btn.sim    { color:#0d6efd; }
  .tel-action-btn.sim:hover    { background:rgba(13,110,253,0.1); }
  .tel-action-btn.del    { color:#dc3545; }
  .tel-action-btn.del:hover    { background:rgba(220,53,69,0.1); }
  .tel-search:focus { border-color:#6d4e36 !important; box-shadow:0 0 0 3px rgba(109,78,54,0.12); }
  .tel-select:focus { border-color:#6d4e36 !important; box-shadow:0 0 0 3px rgba(109,78,54,0.12); }
  .tel-empty { text-align:center; padding:3.5rem 1rem; color:#c0a898; }
  .tel-empty-icon { font-size:2.5rem; margin-bottom:10px; opacity:0.4; }
`;

const card = {
  borderRadius: 14,
  border: '1px solid rgba(109,78,54,0.12)',
  boxShadow: '0 4px 24px rgba(109,78,54,0.07)',
  background: '#fff',
  overflow: 'hidden',
};


const inputStyle = {
  border: '1px solid #d8c8b8', borderRadius: 8,
  fontSize: '0.84rem', padding: '6px 11px',
  width: '100%', outline: 'none', transition: 'border-color 0.15s, box-shadow 0.15s',
};

const btnSm = (color, bg = 'transparent') => ({
  border: `1.5px solid ${color}`, background: bg,
  color: bg === 'transparent' ? color : '#fff',
  borderRadius: 8, padding: '5px 12px', fontSize: '0.8rem',
  cursor: 'pointer', lineHeight: 1.4,
  display: 'inline-flex', alignItems: 'center', gap: 5,
  fontWeight: 600, transition: 'all 0.13s',
});

/* ─── badge statut ────────────────────────────────────────────────────────── */
const StatutBadge = ({ statut }) => {
  const map = {
    'ok':         { color: '#2e7d32', bg: '#e8f5e9', label: 'OK' },
    'suspendu':   { color: '#e67e22', bg: '#fef3e2', label: 'Suspendu' },
    'résilié':    { color: '#dc3545', bg: '#fde8ea', label: 'Résilié' },
    'en attente': { color: '#888',    bg: '#f5f5f5', label: 'En attente' },
  };
  const s = map[statut] || { color: '#888', bg: '#f5f5f5', label: statut || '—' };
  return (
    <span style={{
      display: 'inline-block',
      background: s.bg, color: s.color,
      border: `1px solid ${s.color}55`,
      borderRadius: 6, padding: '2px 8px',
      fontSize: '0.75rem', fontWeight: 700, whiteSpace: 'nowrap'
    }}>{s.label}</span>
  );
};

/* ─── badge société ───────────────────────────────────────────────────────── */
const SocieteBadge = ({ name }) => (
  <span style={{
    display: 'inline-block',
    background: 'rgba(109,78,54,0.1)', color: MAIN,
    border: `1px solid rgba(109,78,54,0.3)`,
    borderRadius: 6, padding: '2px 8px',
    fontSize: '0.75rem', fontWeight: 700, whiteSpace: 'nowrap',
    textTransform: 'uppercase'
  }}>{name || '—'}</span>
);

/* ─── composant principal ─────────────────────────────────────────────────── */
export default function TelephonePage({ user }) {
  const canWrite = user?.role === 'admin' || (user?.privileges || []).includes('telephones:write');

  const [rows,     setRows]     = useState([]);
  const [societes, setSocietes] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');
  const [success,  setSuccess]  = useState('');

  /* ── filtres & tri ── */
  const [search,    setSearch]    = useState('');
  const [fSociete,  setFSociete]  = useState('');
  const [fOper,     setFOper]     = useState('');
  const [fStatut,   setFStatut]   = useState('');
  const [kpiFilter, setKpiFilter] = useState('total');
  const [sortKey,   setSortKey]   = useState('societe');
  const [sortAsc,   setSortAsc]   = useState(true);

  /* ── modals ── */
  const [showModal,    setShowModal]    = useState(false);
  const [editRow,      setEditRow]      = useState(null);
  const [form,         setForm]         = useState(EMPTY_FORM);
  const [saving,       setSaving]       = useState(false);
  const [formError,    setFormError]    = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showImport,   setShowImport]   = useState(false);
  const [showDemande,  setShowDemande]  = useState(false);
  const [showRemise,   setShowRemise]   = useState(false);
  const [remisePrefill, setRemisePrefill] = useState(null);

  /* ── fetch ── */
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [rRows, rSoc] = await Promise.all([
        api.get('/telephones'),
        api.get('/telephones/societes'),
      ]);
      setRows(rRows.data);
      setSocietes(rSoc.data);
    } catch {
      setError('Erreur chargement des données');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* ── stats KPI ── */
  const stats = useMemo(() => {
    const bySociete = {}, byType = {}, byBureau = {}, byOper = {};
    rows.forEach(r => {
      const s = r.societe   || '—'; bySociete[s] = (bySociete[s] || 0) + 1;
      const t = r.colonne1  || '—'; byType[t]    = (byType[t]    || 0) + 1;
      const b = r.bureau    || '—'; byBureau[b]  = (byBureau[b]  || 0) + 1;
      const o = r.operateur || '—'; byOper[o]    = (byOper[o]    || 0) + 1;
    });
    return { total: rows.length, bySociete, byType, byBureau, byOper };
  }, [rows]);

  /* ── liste filtrée + triée ── */
  const displayed = useMemo(() => {
    let list = [...rows];

    if (kpiFilter.startsWith('soc:'))  list = list.filter(r => (r.societe   || '—') === kpiFilter.slice(4));
    if (kpiFilter.startsWith('typ:'))  list = list.filter(r => (r.colonne1  || '—') === kpiFilter.slice(4));
    if (kpiFilter.startsWith('bur:'))  list = list.filter(r => (r.bureau    || '—') === kpiFilter.slice(4));
    if (kpiFilter.startsWith('oper:')) list = list.filter(r => (r.operateur || '—') === kpiFilter.slice(5));

    if (fSociete) list = list.filter(r => r.societe === fSociete);
    if (fOper)    list = list.filter(r => r.operateur === fOper);
    if (fStatut)  list = list.filter(r => r.statut === fStatut);

    const q = search.trim().toLowerCase();
    if (q) list = list.filter(r =>
      r.nom_prenom?.toLowerCase().includes(q) ||
      r.tel?.toLowerCase().includes(q) ||
      r.qualification?.toLowerCase().includes(q) ||
      r.bureau?.toLowerCase().includes(q)
    );

    list.sort((a, b) => {
      const av = a[sortKey] ?? '', bv = b[sortKey] ?? '';
      return sortAsc
        ? String(av).localeCompare(String(bv), 'fr')
        : String(bv).localeCompare(String(av), 'fr');
    });

    return list;
  }, [rows, kpiFilter, fSociete, fOper, fStatut, search, sortKey, sortAsc]);

  const handleSort = (key) => {
    if (sortKey === key) setSortAsc(a => !a);
    else { setSortKey(key); setSortAsc(true); }
  };

  const SortTh = ({ field, children, style: s }) => (
    <th className="tel-th" onClick={() => handleSort(field)} style={s}>
      {children}
      <span style={{ marginLeft: 4, fontSize: '0.55rem', opacity: sortKey === field ? 1 : 0.3 }}>
        {sortKey === field ? (sortAsc ? ' ▲' : ' ▼') : ' ⇅'}
      </span>
    </th>
  );

  /* ── modal add/edit ── */
  const openAdd = () => {
    setEditRow(null);
    setForm(EMPTY_FORM);
    setFormError('');
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditRow(row);
    setForm({
      societe:            row.societe            || '',
      operateur:          row.operateur          || '',
      tel:                row.tel                || '',
      colonne1:           row.colonne1           || '',
      nom_prenom:         row.nom_prenom         || '',
      qualification:      row.qualification      || '',
      statut:             row.statut             || 'ok',
      bureau:             row.bureau             || '',
      forfait:            row.forfait            || '',
      prix:               row.prix               || '',
      observation:        row.observation        || '',
      ancien_utilisateur: row.ancien_utilisateur || '',
    });
    setFormError('');
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.tel) { setFormError('Le numéro de téléphone est obligatoire'); return; }
    setSaving(true);
    setFormError('');
    try {
      if (editRow) {
        await api.put(`/telephones/${editRow.id}`, form);
        setSuccess('Numéro modifié avec succès');
      } else {
        await api.post('/telephones', form);
        setSuccess('Numéro ajouté avec succès');
      }
      setShowModal(false);
      fetchData();
    } catch {
      setFormError("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/telephones/${deleteTarget.id}`);
      setSuccess('Numéro supprimé');
      setDeleteTarget(null);
      fetchData();
    } catch {
      setError('Erreur lors de la suppression');
      setDeleteTarget(null);
    }
  };

  const bureaux = useMemo(() => [...new Set(rows.map(r => r.bureau).filter(Boolean))].sort(), [rows]);
  const hasFilters = search || fSociete || fOper || fStatut || kpiFilter !== 'total';
  const resetKpi = () => setKpiFilter('total');

  const exportExcel = () => {
    const cols = [
      { key: 'societe',            label: 'Société' },
      { key: 'operateur',          label: 'Opérateur' },
      { key: 'tel',                label: 'Téléphone' },
      { key: 'colonne1',           label: 'Type' },
      { key: 'nom_prenom',         label: 'Nom & Prénom' },
      { key: 'qualification',      label: 'Qualification' },
      { key: 'statut',             label: 'Statut' },
      { key: 'bureau',             label: 'Bureau' },
      { key: 'forfait',            label: 'Forfait' },
      { key: 'prix',               label: 'Prix (DH)' },
      { key: 'ancien_utilisateur', label: 'Ancien utilisateur' },
      { key: 'observation',        label: 'Observation' },
    ];
    const data = [
      cols.map(c => c.label),
      ...displayed.map(r => cols.map(c => r[c.key] ?? '')),
    ];
    const ws = XLSX.utils.aoa_to_sheet(data);
    ws['!cols'] = cols.map(() => ({ wch: 20 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Téléphones');
    XLSX.writeFile(wb, `telephones_${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto', padding: '1.5rem 1.25rem 4rem' }}>
      <style>{CSS}</style>

      {/* ── en-tête ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10, marginBottom: '1.25rem' }}>
        <div>
          <h4 style={{ color: MAIN, fontWeight: 700, marginBottom: 2 }}>
            Gestion Numéros Téléphones
          </h4>
          <div style={{ color: '#888', fontSize: '0.82rem' }}>
            {rows.length} numéro{rows.length !== 1 ? 's' : ''} au total
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={fetchData} disabled={loading} style={btnSm(MAIN)}>
            {loading ? <Spinner size="sm" animation="border" /> : <FaSyncAlt />}
            Actualiser
          </button>
          {canWrite && (
            <>
              <button onClick={() => setShowDemande(true)} style={{ ...btnSm('#6f42c1', '#6f42c1'), fontWeight: 700 }}>
                📋 Demande numéro
              </button>
              <button onClick={() => { setRemisePrefill(null); setShowRemise(true); }} style={{ ...btnSm('#0d6efd', '#0d6efd'), fontWeight: 700 }}>
                📱 Fiche remise SIM
              </button>
              <button onClick={() => setShowImport(true)} style={{ ...btnSm('#2e7d32', '#2e7d32'), fontWeight: 700 }}>
                📥 Importer Excel
              </button>
              <button onClick={openAdd} style={{ ...btnSm(MAIN, MAIN), fontWeight: 700 }}>
                <FaPlus /> Ajouter
              </button>
            </>
          )}
        </div>
      </div>

      {/* ── KPI cards ── */}
      {/* Grandes cards : Total + Orange + IAM */}
      <Row className="g-3 mb-3">
        <Col xs={6} sm={4} md={2}>
          <KpiCard title="Total" value={stats.total} color={MAIN} icon={FaPhoneAlt}
            onClick={() => setKpiFilter('total')} active={kpiFilter === 'total'} />
        </Col>
        {stats.byOper['ORANGE'] !== undefined && (
          <Col xs={6} sm={4} md={2}>
            <OperatorKpiCard operateur="ORANGE" value={stats.byOper['ORANGE']} color="#e67e22"
              active={kpiFilter === 'oper:ORANGE'}
              onClick={() => setKpiFilter(kpiFilter === 'oper:ORANGE' ? 'total' : 'oper:ORANGE')} />
          </Col>
        )}
        {stats.byOper['IAM'] !== undefined && (
          <Col xs={6} sm={4} md={2}>
            <OperatorKpiCard operateur="IAM" value={stats.byOper['IAM']} color="#009fe3"
              active={kpiFilter === 'oper:IAM'}
              onClick={() => setKpiFilter(kpiFilter === 'oper:IAM' ? 'total' : 'oper:IAM')} />
          </Col>
        )}
      </Row>

      {/* Chips compactes */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>

        {/* Autres opérateurs */}
        {Object.keys(stats.byOper).filter(o => o !== 'ORANGE' && o !== 'IAM').length > 0 && (
          <MiniGroup label="Opérateur">
            {Object.entries(stats.byOper)
              .filter(([o]) => o !== 'ORANGE' && o !== 'IAM')
              .sort((a,b) => b[1]-a[1])
              .map(([o, cnt], i) => (
                <MiniChip key={o} label={o} value={cnt}
                  color={OPER_COLORS[o] || EXTRA_COLORS[i % EXTRA_COLORS.length]}
                  active={kpiFilter === `oper:${o}`}
                  onClick={() => setKpiFilter(kpiFilter === `oper:${o}` ? 'total' : `oper:${o}`)} />
              ))}
          </MiniGroup>
        )}

        {/* Par société */}
        {Object.keys(stats.bySociete).length > 0 && (
          <MiniGroup label="Société">
            {[...SOC_COLORS.filter(sc => stats.bySociete[sc.name] !== undefined),
              ...Object.keys(stats.bySociete)
                .filter(s => !SOC_COLORS.find(sc => sc.name === s))
                .map((s, i) => ({ name: s, color: EXTRA_COLORS[i % EXTRA_COLORS.length] }))
            ].map(sc => (
              <MiniChip key={sc.name} label={sc.name.toUpperCase()} value={stats.bySociete[sc.name]}
                color={sc.color}
                active={kpiFilter === `soc:${sc.name}`}
                onClick={() => setKpiFilter(kpiFilter === `soc:${sc.name}` ? 'total' : `soc:${sc.name}`)} />
            ))}
          </MiniGroup>
        )}

        {/* Par type */}
        {Object.keys(stats.byType).length > 0 && (
          <MiniGroup label="Type">
            {Object.entries(stats.byType).sort((a,b) => b[1]-a[1]).map(([t, cnt], i) => (
              <MiniChip key={t} label={t} value={cnt}
                color={EXTRA_COLORS[i % EXTRA_COLORS.length]}
                active={kpiFilter === `typ:${t}`}
                onClick={() => setKpiFilter(kpiFilter === `typ:${t}` ? 'total' : `typ:${t}`)} />
            ))}
          </MiniGroup>
        )}

        {/* Par bureau */}
        {Object.keys(stats.byBureau).length > 0 && (
          <MiniGroup label="Bureau">
            {Object.entries(stats.byBureau).sort((a,b) => b[1]-a[1]).map(([b, cnt], i) => (
              <MiniChip key={b} label={b.toUpperCase()} value={cnt}
                color={SOC_COLORS[i % SOC_COLORS.length].color}
                active={kpiFilter === `bur:${b}`}
                onClick={() => setKpiFilter(kpiFilter === `bur:${b}` ? 'total' : `bur:${b}`)} />
            ))}
          </MiniGroup>
        )}
      </div>

      {/* ── alertes ── */}
      {error   && <Alert variant="danger"  dismissible onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

      {/* ── tableau ── */}
      <div style={card}>

        {/* barre de filtres */}
        <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #f5ede8', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <FaFilter style={{ color: MAIN, fontSize: '0.8rem', flexShrink: 0 }} />

          <div style={{ position: 'relative', flex: '1 1 200px', minWidth: 150 }}>
            <FaSearch style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: '#bbb', fontSize: '0.75rem' }} />
            <input
              placeholder="Recherche nom / tél / qualification…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ ...inputStyle, paddingLeft: 28 }}
            />
          </div>

          <select value={fSociete} onChange={e => setFSociete(e.target.value)}
            style={{ ...inputStyle, width: 'auto', minWidth: 130 }}>
            <option value="">Toutes sociétés</option>
            {societes.map(s => <option key={s} value={s}>{s.toUpperCase()}</option>)}
          </select>

          <select value={fOper} onChange={e => setFOper(e.target.value)}
            style={{ ...inputStyle, width: 'auto', minWidth: 120 }}>
            <option value="">Tous opérateurs</option>
            {OPERATEURS.map(o => <option key={o} value={o}>{o}</option>)}
          </select>

          <select value={fStatut} onChange={e => setFStatut(e.target.value)}
            style={{ ...inputStyle, width: 'auto', minWidth: 120 }}>
            <option value="">Tous statuts</option>
            {STATUTS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>

          {hasFilters && (
            <button
              onClick={() => { setSearch(''); setFSociete(''); setFOper(''); setFStatut(''); resetKpi(); }}
              style={{ ...btnSm('#dc3545'), fontSize: '0.75rem' }}
            >
              ✕ Reset
            </button>
          )}

          <span style={{ marginLeft: 'auto', fontSize: '0.78rem', color: '#999' }}>
            {displayed.length} résultat{displayed.length !== 1 ? 's' : ''}
          </span>

          <button onClick={exportExcel} disabled={displayed.length === 0}
            style={{ ...btnSm('#2e7d32'), fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
            ⬇️ Exporter Excel
          </button>
        </div>

        {/* table */}
        {loading ? (
          <TableSkeleton rows={8} cols={12} hasActions={canWrite} />
        ) : (
          <div style={{ overflowX: 'auto', maxHeight: '60vh', overflowY: 'auto' }}>
            <table className="tel-table">
              <thead>
                <tr>
                  <SortTh field="societe">Société</SortTh>
                  <SortTh field="operateur">Opérateur</SortTh>
                  <SortTh field="tel">Téléphone</SortTh>
                  <th className="tel-th">Type</th>
                  <SortTh field="nom_prenom">Nom & Prénom</SortTh>
                  <SortTh field="qualification">Qualification</SortTh>
                  <SortTh field="statut">Statut</SortTh>
                  <SortTh field="bureau">Bureau</SortTh>
                  <th className="tel-th">Forfait</th>
                  <SortTh field="prix">Prix</SortTh>
                  <th className="tel-th">Observation</th>
                  {canWrite && <th className="tel-th" style={{ textAlign: 'center', width: 100 }}>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {displayed.length === 0 ? (
                  <tr>
                    <td colSpan={canWrite ? 12 : 11}>
                      <div className="tel-empty">
                        <div className="tel-empty-icon">📭</div>
                        <div style={{ fontWeight: 600, marginBottom: 4 }}>Aucun résultat</div>
                        <div style={{ fontSize: '0.8rem' }}>Modifiez les filtres ou ajoutez un numéro</div>
                      </div>
                    </td>
                  </tr>
                ) : displayed.map((r, i) => (
                  <tr key={r.id} className="tel-tr" style={{ background: i % 2 === 0 ? '#fff' : '#fdfaf8' }}>
                    <td className="tel-td"><SocieteBadge name={r.societe} /></td>
                    <td className="tel-td">
                      <span style={{ fontWeight: 700, fontSize: '0.82rem', color: MAIN, letterSpacing: '0.02em' }}>
                        {r.operateur || '—'}
                      </span>
                    </td>
                    <td className="tel-td">
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}>
                        <span style={{ fontWeight: 700, fontFamily: 'monospace', fontSize: '0.9rem', letterSpacing: '0.03em' }}>
                          {r.tel || '—'}
                        </span>
                        {r.tel && (
                          <>
                            <a href={`tel:${r.tel}`} title="Appeler"
                              style={{ color: '#2e7d32', fontSize: '0.85rem', lineHeight: 1, display: 'flex', opacity: 0.8, transition: 'opacity 0.12s' }}
                              onClick={e => e.stopPropagation()}>
                              <FaPhoneAlt />
                            </a>
                            <a href={`https://wa.me/${toWaNumber(r.tel)}`}
                              target="_blank" rel="noreferrer" title="WhatsApp"
                              style={{ color: '#25d366', fontSize: '0.95rem', lineHeight: 1, display: 'flex', opacity: 0.8, transition: 'opacity 0.12s' }}
                              onClick={e => e.stopPropagation()}>
                              <FaWhatsapp />
                            </a>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="tel-td" style={{ color: '#999', fontSize: '0.79rem' }}>{r.colonne1 || '—'}</td>
                    <td className="tel-td" style={{ fontWeight: 500 }}>{r.nom_prenom || '—'}</td>
                    <td className="tel-td" style={{ color: '#777', fontSize: '0.82rem' }}>{r.qualification || '—'}</td>
                    <td className="tel-td"><StatutBadge statut={r.statut} /></td>
                    <td className="tel-td" style={{ color: '#666', fontSize: '0.82rem' }}>{r.bureau || '—'}</td>
                    <td className="tel-td" style={{ fontSize: '0.81rem', color: '#666' }}>{r.forfait || '—'}</td>
                    <td className="tel-td">
                      {r.prix != null
                        ? <span style={{ fontWeight: 700, color: MAIN }}>
                            {Number(r.prix).toFixed(2)}
                            <span style={{ fontSize: '0.72rem', color: '#aaa', marginLeft: 2 }}>DH</span>
                          </span>
                        : <span style={{ color: '#ddd' }}>—</span>}
                    </td>
                    <td className="tel-td" style={{ maxWidth: 170 }}>
                      <span title={r.observation} style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#999', fontSize: '0.79rem' }}>
                        {r.observation || <span style={{ color: '#e0d5ce' }}>—</span>}
                      </span>
                    </td>
                    {canWrite && (
                      <td className="tel-td" style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                        <button className="tel-action-btn edit" onClick={() => openEdit(r)} title="Modifier">
                          <FaEdit size={13} />
                        </button>
                        <button className="tel-action-btn sim"
                          onClick={() => { setRemisePrefill(r); setShowRemise(true); }}
                          title="Fiche remise SIM">
                          📱
                        </button>
                        <button className="tel-action-btn del" onClick={() => setDeleteTarget(r)} title="Supprimer">
                          <FaTrash size={13} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ padding: '0.4rem 1rem', borderTop: '1px solid #f5ede8', fontSize: '0.75rem', color: '#bbb' }}>
          {displayed.length} / {rows.length} lignes affichées
        </div>
      </div>

      {/* ── Modal ajout / édition ── */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Modal.Header closeButton style={{ borderBottom: '2px solid #f5ede8' }}>
          <Modal.Title style={{ color: MAIN, fontWeight: 700, fontSize: '1.05rem' }}>
            {editRow ? <><FaEdit style={{ marginRight: 8 }} />Modifier le numéro</> : <><FaPlus style={{ marginRight: 8 }} />Ajouter un numéro</>}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {formError && <Alert variant="danger" className="py-2">{formError}</Alert>}
          <Row className="g-3">
            <Col md={6}>
              <label style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 4, display: 'block' }}>Société</label>
              <select style={inputStyle} value={form.societe} onChange={e => setForm(f => ({ ...f, societe: e.target.value }))}>
                <option value="">— Sélectionner —</option>
                {societes.map(s => <option key={s} value={s}>{s.toUpperCase()}</option>)}
              </select>
            </Col>
            <Col md={6}>
              <label style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 4, display: 'block' }}>Opérateur</label>
              <select style={inputStyle} value={form.operateur} onChange={e => setForm(f => ({ ...f, operateur: e.target.value }))}>
                <option value="">— Sélectionner —</option>
                {OPERATEURS.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </Col>
            <Col md={4}>
              <label style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 4, display: 'block' }}>
                Numéro <span style={{ color: '#dc3545' }}>*</span>
              </label>
              <input style={inputStyle} value={form.tel} onChange={e => setForm(f => ({ ...f, tel: e.target.value }))} placeholder="ex: 0662692316" />
            </Col>
            <Col md={2}>
              <label style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 4, display: 'block' }}>Type</label>
              <input style={inputStyle} value={form.colonne1} onChange={e => setForm(f => ({ ...f, colonne1: e.target.value }))} placeholder="P / T…" />
            </Col>
            <Col md={6}>
              <label style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 4, display: 'block' }}>Nom & Prénom</label>
              <input style={inputStyle} value={form.nom_prenom} onChange={e => setForm(f => ({ ...f, nom_prenom: e.target.value }))} />
            </Col>
            <Col md={4}>
              <label style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 4, display: 'block' }}>Qualification</label>
              <input style={inputStyle} value={form.qualification} onChange={e => setForm(f => ({ ...f, qualification: e.target.value }))} />
            </Col>
            <Col md={4}>
              <label style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 4, display: 'block' }}>Statut</label>
              <select style={inputStyle} value={form.statut} onChange={e => setForm(f => ({ ...f, statut: e.target.value }))}>
                {STATUTS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </Col>
            <Col md={4}>
              <label style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 4, display: 'block' }}>Bureau</label>
              <input style={inputStyle} value={form.bureau} onChange={e => setForm(f => ({ ...f, bureau: e.target.value }))} list="bureaux-list" />
              <datalist id="bureaux-list">
                {bureaux.map(b => <option key={b} value={b} />)}
              </datalist>
            </Col>
            <Col md={4}>
              <label style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 4, display: 'block' }}>Forfait</label>
              <input style={inputStyle} value={form.forfait} onChange={e => setForm(f => ({ ...f, forfait: e.target.value }))} placeholder="ex: 30H+15G" />
            </Col>
            <Col md={4}>
              <label style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 4, display: 'block' }}>Prix (DH)</label>
              <input type="number" step="0.01" min="0" style={inputStyle} value={form.prix} onChange={e => setForm(f => ({ ...f, prix: e.target.value }))} />
            </Col>
            <Col md={6}>
              <label style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 4, display: 'block' }}>Ancien utilisateur</label>
              <input style={inputStyle} value={form.ancien_utilisateur} onChange={e => setForm(f => ({ ...f, ancien_utilisateur: e.target.value }))} />
            </Col>
            <Col xs={12}>
              <label style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 4, display: 'block' }}>Observation</label>
              <textarea rows={2} style={{ ...inputStyle, resize: 'vertical' }} value={form.observation} onChange={e => setForm(f => ({ ...f, observation: e.target.value }))} />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: '1px solid #f5ede8' }}>
          <button style={btnSm('#888')} onClick={() => setShowModal(false)}>Annuler</button>
          <button style={{ ...btnSm(MAIN, MAIN), fontWeight: 700, padding: '6px 18px' }} onClick={handleSave} disabled={saving}>
            {saving ? <Spinner size="sm" animation="border" /> : null}
            {saving ? ' Enregistrement…' : 'Enregistrer'}
          </button>
        </Modal.Footer>
      </Modal>

      {/* ── Modal confirmation suppression ── */}
      <Modal show={!!deleteTarget} onHide={() => setDeleteTarget(null)} size="sm" centered>
        <Modal.Header closeButton style={{ borderBottom: '1px solid #f5ede8' }}>
          <Modal.Title style={{ color: '#dc3545', fontWeight: 700, fontSize: '1rem' }}>
            Confirmer la suppression
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: '0.9rem' }}>
          Supprimer le numéro <strong>{deleteTarget?.tel}</strong> ({deleteTarget?.nom_prenom || '—'}) ?
          <div style={{ color: '#dc3545', fontSize: '0.8rem', marginTop: 6 }}>Cette action est irréversible.</div>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: '1px solid #f5ede8' }}>
          <button style={btnSm('#888')} onClick={() => setDeleteTarget(null)}>Annuler</button>
          <button style={{ ...btnSm('#dc3545', '#dc3545'), fontWeight: 700 }} onClick={handleDelete}>
            <FaTrash /> Supprimer
          </button>
        </Modal.Footer>
      </Modal>

      {/* ── Modal import Excel ── */}
      <TelephoneImportModal
        show={showImport}
        onHide={() => setShowImport(false)}
        onSuccess={() => { setShowImport(false); fetchData(); setSuccess('Import terminé avec succès'); }}
      />

      {/* ── Modal demande nouveau numéro ── */}
      <DemandeNumeroModal
        show={showDemande}
        onHide={() => setShowDemande(false)}
        societes={societes}
      />

      {/* ── Modal fiche remise SIM ── */}
      <FicheRemiseModal
        show={showRemise}
        onHide={() => setShowRemise(false)}
        prefill={remisePrefill}
        societes={societes}
        allRows={rows}
      />
    </div>
  );
}
